import { action } from 'mobx'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type {
  SnackbarFactory,
  SnackbarRetryWrapperFactory,
} from '@/features/shell'
import type { GetCurrentMemberFn } from '@/features/iam'
import { XeroConnectionsDialogViewModel } from '@/features/bookkeeping/components/BookkeepingPlatformConnectionsDialog/XeroConnectionsDialogViewModel'
import { QuickBooksConnectionsDialogViewModel } from '@/features/bookkeeping/components/BookkeepingPlatformConnectionsDialog/QuickBooksConnectionsDialogViewModel'
import { BookkeepingOtherConnectionDialogViewModel } from '@/features/bookkeeping/components/BookkeepingOtherConnectionDialog/BookkeepingOtherConnectionDialog'
import type { BookkeepingPlatform } from '@/features/bookkeeping/platform-connections/bookkeeping-platform'
import type {
  QuickBooksAccount,
  XeroAccount,
} from '@/features/bookkeeping/platform-connections/BookkeepingPlatformConnection'
import type { AvailableBookkeepingSoftwareType } from '@/features/bookkeeping/platform-connections/available-bookkeeping-software'
import type { BookkeepingPlatformConnectionStore } from '@/features/bookkeeping/platform-connections/BookkeepingPlatformConnectionStore'

/**
 * The account to provide.
 */
export type PlatformConnectionSelection =
  | QuickBooksAccountSelection
  | XeroAccountSelection
  | OtherAccountSelection

/**
 * The QuickBooks account to provide.
 */
interface QuickBooksAccountSelection {
  type: 'QuickBooks'
  account: QuickBooksAccount
}

/**
 * The Xero account to provide.
 */
interface XeroAccountSelection {
  type: 'Xero'
  account: XeroAccount
}

/**
 * The Other account to provide.
 */
interface OtherAccountSelection {
  type: 'Other'
  software: AvailableBookkeepingSoftwareType
}

/**
 * The bookkeeping connection target.
 */
export interface BookkeepingConnectionTarget {
  returnUrl: () => URL
}

export interface BookkeepingPlatformSelectionDialogViewModelOptions {
  /**
   * The bookkeeping connection target.
   */
  target: BookkeepingConnectionTarget

  /**
   * The function to call when the user provides a QuickBooks connection.
   */
  onProvideAccount: (
    quickBooksAccount: PlatformConnectionSelection,
  ) => Promise<unknown>
}

export class BookkeepingPlatformSelectionDialogViewModel
  implements ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The modal.
   */
  readonly modal = new ModalViewModel()

  /**
   * QuickBooks connections view model.
   */
  quickBooksConnectionsDialogVm: QuickBooksConnectionsDialogViewModel

  /**
   * Xero connections view model.
   */
  xeroConnectionsDialogVm: XeroConnectionsDialogViewModel

  /**
   * Other platform connection view model.
   */
  bookkeepingOtherConnectionsDialogVm: BookkeepingOtherConnectionDialogViewModel

  constructor(
    private readonly _options: BookkeepingPlatformSelectionDialogViewModelOptions,
    private readonly _store: BookkeepingPlatformConnectionStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    private readonly _newSnackbar: SnackbarFactory,
  ) {
    this.quickBooksConnectionsDialogVm =
      new QuickBooksConnectionsDialogViewModel(
        {
          target: this._options.target,
          onAction: (account: QuickBooksAccount) =>
            this._options.onProvideAccount({
              type: 'QuickBooks',
              account,
            }),
        },
        this._store,
        this._createSnackbarRetryWrapper,
        this._newSnackbar,
        this._getCurrentMember,
      )

    this.xeroConnectionsDialogVm = new XeroConnectionsDialogViewModel(
      {
        target: this._options.target,
        onAction: (account: XeroAccount) =>
          this._options.onProvideAccount({
            type: 'Xero',
            account,
          }),
      },
      this._store,
      this._createSnackbarRetryWrapper,
      this._newSnackbar,
    )

    this.bookkeepingOtherConnectionsDialogVm =
      new BookkeepingOtherConnectionDialogViewModel(
        {
          onAction: (software) =>
            this._options.onProvideAccount({
              type: 'Other',
              software,
            }),
        },
        this._newSnackbar,
      )

    this._lifecycle.manage(this.xeroConnectionsDialogVm)
    this._lifecycle.manage(this.quickBooksConnectionsDialogVm)
    this._lifecycle.manage(this.bookkeepingOtherConnectionsDialogVm)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the dialog depending on the platform selected.
   */
  @action.bound
  selectPlatform(platform: BookkeepingPlatform) {
    switch (platform) {
      case 'QuickBooks':
        return this.quickBooksConnectionsDialogVm.show()
      case 'Xero':
        return this.xeroConnectionsDialogVm.show()
      case 'Other':
        return this.bookkeepingOtherConnectionsDialogVm.show()
    }
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  show() {
    this.modal.show()
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  close() {
    this.quickBooksConnectionsDialogVm.close()
    this.xeroConnectionsDialogVm.close()
    this.bookkeepingOtherConnectionsDialogVm.close()
    this.modal.close()
  }
}
